import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaEye,
  FaFileExport,
  FaFilter,
  FaFolderOpen,
  FaShieldAlt,
  FaSpinner,
  FaSyncAlt,
  FaTag,
  FaTimes,
  FaBan,
  FaUndoAlt,
  FaColumns,
  FaDownload,
  FaTrashAlt,
  FaUsers,
} from 'react-icons/fa';
import ProductExportService, {
  EXPORT_JOB_STORAGE_KEY,
  BULK_SUBSCRIBE_JOB_STORAGE_KEY,
} from '../../../service/admin/productExportService';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import {
  BulkSubscribeConfirmModal,
  BulkSubscribeJobFilterModal,
  BulkSubscribeProgress,
  BulkSubscribeUserPicker,
  SubscriptionHistoryPanel,
  useBulkSubscribe,
} from './bulkSubscribe';
import './style.css';

/** Job IDs for which the admin successfully saved the export ZIP in this browser (S3 file may still exist). */
const PE_ZIP_DOWNLOADED_IDS_KEY = 'pe_export_zip_downloaded_job_ids';
const PE_ZIP_DOWNLOADED_IDS_MAX = 120;
/** Export jobs table: page size for list + "Show more" loads. */
const EXPORT_JOBS_PAGE_SIZE = 20;
/** Batch ID picker: page size for export filter dropdown + “Show more”. */
const PE_BATCH_PAGE_SIZE = 10;
/** Tag picker: first page + each "Show more" (append below, no full-panel refresh). */
const TAG_SUGGESTIONS_PAGE_SIZE = 100;

function loadZipDownloadedJobIdsFromStorage() {
  try {
    const raw = sessionStorage.getItem(PE_ZIP_DOWNLOADED_IDS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

function persistZipDownloadedJobIdsSet(idSet) {
  const arr = [...idSet].slice(-PE_ZIP_DOWNLOADED_IDS_MAX);
  try {
    sessionStorage.setItem(PE_ZIP_DOWNLOADED_IDS_KEY, JSON.stringify(arr));
  } catch {
    /* ignore quota / private mode */
  }
}

const statusOptions = [
  { value: '', label: 'All' },
  { value: '5', label: 'List' },
  { value: '6', label: 'Draft' },
];
const whiteImageOptions = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];
const dropshippingAvailableOptions = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];
const variantQualityOptions = [
  { value: '', label: 'All' },
  { value: 'weight_available', label: 'Weight Available' },
  { value: 'dimension_available', label: 'Dimension Available' },
  { value: 'both_available', label: 'Weight and Dimension Both Available' },
  { value: 'either_available', label: 'Weight or Dimension Available' },
  { value: 'weight_missing', label: 'Weight Missing' },
  { value: 'dimension_missing', label: 'Dimension Missing' },
  { value: 'either_missing', label: 'Weight or Dimension Missing' },
  { value: 'both_missing', label: 'Weight and Dimension Both Missing' },
];

/** Non-empty snapshot value (filters saved on export job). */
function exportJobSnapHas(v) {
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'boolean') return v;
  return String(v ?? '').trim() !== '';
}

/**  `filters` (labels match the export filter form). */
function buildExportJobSnapshotFilterLines(filters) {
  const f = filters && typeof filters === 'object' && !Array.isArray(filters) ? filters : {};
  const lines = [];
  if (exportJobSnapHas(f.imgUrlLengthFrom) || exportJobSnapHas(f.imgUrlLengthTo)) {
    const from = String(f.imgUrlLengthFrom ?? '').trim();
    const to = String(f.imgUrlLengthTo ?? '').trim();
    let value = '';
    if (from && to) value = `${from} – ${to}`;
    else if (from) value = `from ${from}`;
    else value = `to ${to}`;
    lines.push({ label: 'imgUrl length range', value });
  } else if (exportJobSnapHas(f.imgUrlGreaterThan)) {
    const v = String(f.imgUrlGreaterThan) === '' ? 'Any' : String(f.imgUrlGreaterThan);
    lines.push({ label: 'imgUrl length greater than', value: v });
  }
  if (exportJobSnapHas(f.variationsLengthFrom) || exportJobSnapHas(f.variationsLengthTo)) {
    const from = String(f.variationsLengthFrom ?? '').trim();
    const to = String(f.variationsLengthTo ?? '').trim();
    let value = '';
    if (from && to) value = `${from} – ${to}`;
    else if (from) value = `from ${from}`;
    else value = `to ${to}`;
    lines.push({ label: 'Variations length range', value });
  } else if (exportJobSnapHas(f.variationsGreaterThan)) {
    const v = String(f.variationsGreaterThan) === '' ? 'Any' : String(f.variationsGreaterThan);
    lines.push({ label: 'Variations length greater than', value: v });
  }
  if (exportJobSnapHas(f.variantQualityMode)) {
    const label =
      variantQualityOptions.find((o) => o.value === f.variantQualityMode)?.label || String(f.variantQualityMode);
    lines.push({ label: 'Weight/Dimensions', value: label });
  }
  if (exportJobSnapHas(f.status)) {
    const opt = statusOptions.find((o) => String(o.value) === String(f.status));
    lines.push({ label: 'Status', value: opt ? opt.label : String(f.status) });
  }
  if (exportJobSnapHas(f.isWhiteImageAvailable)) {
    const opt = whiteImageOptions.find((o) => String(o.value) === String(f.isWhiteImageAvailable));
    lines.push({
      label: 'White image available',
      value: opt ? opt.label : String(f.isWhiteImageAvailable),
    });
  }
  if (exportJobSnapHas(f.isDropshippingAvailable)) {
    const opt = dropshippingAvailableOptions.find((o) => String(o.value) === String(f.isDropshippingAvailable));
    lines.push({
      label: 'Dropshipping available',
      value: opt ? opt.label : String(f.isDropshippingAvailable),
    });
  }
  if (exportJobSnapHas(f.batchIds)) lines.push({ label: 'Batch ID(s)', value: String(f.batchIds) });
  if (exportJobSnapHas(f.moqFrom) || exportJobSnapHas(f.moqTo)) {
    const from = String(f.moqFrom ?? '').trim();
    const to = String(f.moqTo ?? '').trim();
    let value = '';
    if (from && to) value = `${from} – ${to}`;
    else if (from) value = `from ${from}`;
    else value = `to ${to}`;
    lines.push({ label: 'MOQ range', value });
  }
  if (exportJobSnapHas(f.skus)) lines.push({ label: 'SKU(s)', value: String(f.skus) });
  if (exportJobSnapHas(f.createdAtFrom)) {
    lines.push({ label: 'Created From', value: String(f.createdAtFrom) });
  }
  if (exportJobSnapHas(f.createdAtTo)) {
    lines.push({ label: 'Created To', value: String(f.createdAtTo) });
  }
  if (exportJobSnapHas(f.updatedAtFrom)) {
    lines.push({ label: 'Updated From', value: String(f.updatedAtFrom) });
  }
  if (exportJobSnapHas(f.updatedAtTo)) {
    lines.push({ label: 'Updated To', value: String(f.updatedAtTo) });
  }
  if (exportJobSnapHas(f.categoryIds)) {
    lines.push({ label: 'Category IDs (comma-separated)', value: String(f.categoryIds) });
  }
  if (exportJobSnapHas(f.topCategoryIds)) {
    lines.push({ label: 'China Top Category IDs', value: String(f.topCategoryIds) });
  }
  if (exportJobSnapHas(f.secondCategoryIds)) {
    lines.push({ label: 'China Second Category IDs', value: String(f.secondCategoryIds) });
  }
  if (exportJobSnapHas(f.thirdCategoryIds)) {
    lines.push({ label: 'China Third Category IDs', value: String(f.thirdCategoryIds) });
  }
  if (Array.isArray(f.tags) && f.tags.length) {
    lines.push({ label: 'Tags', value: f.tags.map(String).join(', ') });
  }
  // Category mapped by user filter — disabled (see mapped-user UI block).
  // if (exportJobSnapHas(f.mappedByUserId)) {
  //   lines.push({ label: 'Mapped User', value: String(f.mappedByUserId) });
  // }
  if (f.categoryIdRequired) lines.push({ label: 'Category ID required', value: 'Yes' });
  if (f.descriptionRequired) lines.push({ label: 'Description required', value: 'Yes' });
  if (f.imgDescriptionRequired) lines.push({ label: 'Image description required', value: 'Yes' });
  if (f.keywordSearchRequired) lines.push({ label: 'Search keyword required', value: 'Yes' });
  if (f.moq1Required) lines.push({ label: 'MOQ 1 required', value: 'Yes' });
  return lines;
}

const formatExportJobDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '—';
  }
};

/** Duration in seconds (SKU upload style). */
const formatExportDurationSec = (startIso, endIso) => {
  if (!startIso || !endIso) return '—';
  const s = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 1000);
  if (Number.isNaN(s)) return '—';
  if (s === 0) return '0s';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

const EXPORT_JOB_STATUS_LABEL = {
  pending: 'Queued',
  processing: 'Processing',
  completed: 'Completed',
  finished: 'Finished',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const formatExportJobStatusLabel = (status) =>
  EXPORT_JOB_STATUS_LABEL[status] || String(status || 'Unknown').replace(/_/g, ' ');

const exportJobStatusBadgeModifiers = {
  pending: 'pending',
  processing: 'processing',
  completed: 'completed',
  finished: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
};

/** Keep history table in sync with status API (no continuous export-jobs polling). */
const mergeExportJobsRowFromStatus = (prev, jobId, d) => {
  const sid = String(jobId);
  const idx = prev.findIndex((j) => String(j.exportJobId) === sid);
  if (idx < 0) return prev;
  const row = prev[idx];
  const totalChunks = Math.max(
    1,
    Number(d.totalChunks) ||
      (row.totalChunks != null ? Number(row.totalChunks) : NaN) ||
      1,
  );
  const chunksDelivered = Number(
    d.chunksDelivered != null ? d.chunksDelivered : row.chunksDelivered ?? 0,
  );
  const downloadAvailable = Boolean(d.downloadAvailable);
  const hasLocalFile = d.status === 'completed' && downloadAvailable;
  const delivered =
    d.status === 'cancelled'
      ? false
      : d.status === 'finished' ||
        (d.status === 'completed' && !hasLocalFile && totalChunks <= 1);
  const awaitingFileDrop = Boolean(hasLocalFile);
  let totalProducts = row.totalProducts;
  if (d.totalProducts != null && `${d.totalProducts}` !== '') {
    const tn = Number(d.totalProducts);
    if (!Number.isNaN(tn)) totalProducts = tn;
  }
  const downloadFileName =
    d.fileName && String(d.fileName).trim()
      ? String(d.fileName).trim().replace(/\//g, '_').replace(/\\/g, '_').slice(0, 200)
      : row.downloadFileName;
  const next = [...prev];
  next[idx] = {
    ...row,
    status: d.status,
    processedProducts:
      d.processedProducts != null ? Number(d.processedProducts) : row.processedProducts,
    totalProducts,
    totalChunks,
    chunksDelivered,
    delivered,
    awaitingFileDrop,
    downloadFileName,
    errorMessage: d.errorMessage != null ? d.errorMessage : row.errorMessage,
    updatedAt: d.updatedAt || row.updatedAt,
  };
  return next;
};

const initialFilters = {
  imgUrlLengthFrom: '',
  imgUrlLengthTo: '',
  variationsLengthFrom: '',
  variationsLengthTo: '',
  variantQualityMode: '',
  categoryIdRequired: false,
  descriptionRequired: false,
  imgDescriptionRequired: false,
  keywordSearchRequired: false,
  moq1Required: false,
  status: '',
  isWhiteImageAvailable: '',
  isDropshippingAvailable: '',
  createdAtFrom: '',
  createdAtTo: '',
  updatedAtFrom: '',
  updatedAtTo: '',
  categoryIds: '',
  topCategoryIds: '',
  secondCategoryIds: '',
  thirdCategoryIds: '',
  batchIds: '',
  moqFrom: '',
  moqTo: '',
  skus: '',
  tags: [],
  // mappedByUserId: '', // Category mapped by user — filter removed from UI
};

const ProductExport = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [counting, setCounting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [countStats, setCountStats] = useState(null);

  /** DB-backed export job (survives navigation). */
  const [exportJobId, setExportJobId] = useState(null);
  const [exportJobStatus, setExportJobStatus] = useState('idle'); // idle | pending | processing | completed | failed
  const [exportProcessed, setExportProcessed] = useState(0);
  /** From job document (status API) — matched product count for current filters. */
  const [exportTotalProducts, setExportTotalProducts] = useState(null);
  const [exportTotalChunks, setExportTotalChunks] = useState(null);
  const [exportFilePartNumber, setExportFilePartNumber] = useState(null);
  const [exportProgressEvery, setExportProgressEvery] = useState(1000);
  const [exportStarting, setExportStarting] = useState(false);
  const exportPollRef = useRef(null);
  const autoDownloadKickRef = useRef(null);
  const [autoDownloadBusy, setAutoDownloadBusy] = useState(false);
  const [removingJobId, setRemovingJobId] = useState(null);
  const [deletingStoredExportJobId, setDeletingStoredExportJobId] = useState(null);
  const fetchExportJobStatusRef = useRef(null);

  const [exportJobs, setExportJobs] = useState([]);
  const [exportJobsHasMore, setExportJobsHasMore] = useState(false);
  const [exportJobsLoadingMore, setExportJobsLoadingMore] = useState(false);
  const exportJobsLengthRef = useRef(0);
  const zipDownloadedJobIdsRef = useRef(loadZipDownloadedJobIdsFromStorage());
  const [zipDownloadedEpoch, setZipDownloadedEpoch] = useState(0);
  const [manualDownloadingJobId, setManualDownloadingJobId] = useState(null);
  const [exportJobFilterModalJob, setExportJobFilterModalJob] = useState(null);
  const [exportFieldsOpen, setExportFieldsOpen] = useState(false);
  const [exportFieldCatalog, setExportFieldCatalog] = useState(null);
  const [exportFieldCatalogLoading, setExportFieldCatalogLoading] = useState(false);
  const [productFieldChecked, setProductFieldChecked] = useState({});
  const [variantFieldChecked, setVariantFieldChecked] = useState({});

  useEffect(() => {
    exportJobsLengthRef.current = exportJobs.length;
  }, [exportJobs.length]);

  const fetchExportJobs = useCallback(async () => {
    try {
      const res = await ProductExportService.listExportJobs(EXPORT_JOBS_PAGE_SIZE, 0, 'export');
      if (res?.status?.code === 200 && Array.isArray(res.data?.jobs)) {
        setExportJobs(res.data.jobs);
        const hasMore =
          typeof res.data?.hasMore === 'boolean'
            ? res.data.hasMore
            : res.data.jobs.length >= EXPORT_JOBS_PAGE_SIZE;
        setExportJobsHasMore(hasMore);
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  const loadMoreExportJobs = useCallback(async () => {
    if (!exportJobsHasMore || exportJobsLoadingMore) return;
    const skip = exportJobsLengthRef.current;
    setExportJobsLoadingMore(true);
    try {
      const res = await ProductExportService.listExportJobs(EXPORT_JOBS_PAGE_SIZE, skip, 'export');
      if (res?.status?.code === 200 && Array.isArray(res.data?.jobs)) {
        const batch = res.data.jobs;
        const scrollToId = batch[0]?.exportJobId;
        const hasMore =
          typeof res.data?.hasMore === 'boolean'
            ? res.data.hasMore
            : batch.length >= EXPORT_JOBS_PAGE_SIZE;
        setExportJobs((prev) => [...prev, ...batch]);
        setExportJobsHasMore(hasMore);
        if (scrollToId) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              document.getElementById(`pe-export-job-row-${scrollToId}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            });
          });
        }
      }
    } catch (_) {
      /* ignore */
    } finally {
      setExportJobsLoadingMore(false);
    }
  }, [exportJobsHasMore, exportJobsLoadingMore]);

  const rememberZipDownloadedToBrowser = useCallback((jobId) => {
    const sid = String(jobId || '').trim();
    if (!sid) return;
    if (!zipDownloadedJobIdsRef.current.has(sid)) {
      zipDownloadedJobIdsRef.current.add(sid);
      persistZipDownloadedJobIdsSet(zipDownloadedJobIdsRef.current);
    }
    setZipDownloadedEpoch((e) => e + 1);
  }, []);

  const downloadExportZipFile = useCallback(async (jobId, fileName, opts = {}) => {
    const { skipStatusRefresh = false } = opts;
    const token = getAuthLocalStorage()?.loginHeader;
    if (!token) {
      setErrorMessage('Session expired. Please login again.');
      throw new Error('Session expired');
    }
    const url = `${ProductExportService.getExportJobDownloadUrl(jobId)}?ts=${encodeURIComponent(Date.now())}`;
    const dlRes = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
    if (!dlRes.ok) {
      setErrorMessage('Download failed. Try again or use Remove and start a new export.');
      throw new Error('Download failed');
    }
    const blob = await dlRes.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName || 'products_export.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
    rememberZipDownloadedToBrowser(jobId);
    if (!skipStatusRefresh) {
      fetchExportJobStatusRef.current?.(jobId);
    }
    await fetchExportJobs();
  }, [fetchExportJobs, rememberZipDownloadedToBrowser]);

  /** After export completes: one browser download, then clear active job (polling already stopped). */
  const runCompletedExportAutoDownload = useCallback(
    async (jobId, fileName) => {
      const sid = String(jobId || '').trim();
      if (!sid) return;
      setAutoDownloadBusy(true);
      setErrorMessage('');
      try {
        await downloadExportZipFile(sid, fileName, { skipStatusRefresh: true });
      } catch (err) {
        if (err?.message !== 'Session expired' && err?.message !== 'Download failed') {
          setErrorMessage(err?.message || 'Download failed');
        }
      } finally {
        setAutoDownloadBusy(false);
        sessionStorage.removeItem(EXPORT_JOB_STORAGE_KEY);
        setExportJobId(null);
        setExportJobStatus('idle');
        autoDownloadKickRef.current = null;
        fetchExportJobs();
      }
    },
    [downloadExportZipFile, fetchExportJobs],
  );

  // Tag picker state
  const [tagSearch, setTagSearch] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagTotalCount, setTagTotalCount] = useState(0);
  const [tagSearching, setTagSearching] = useState(false);
  const [tagLoadingMore, setTagLoadingMore] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const tagBoxRef = useRef(null);
  const tagGridScrollRef = useRef(null);
  const tagSearchTimer = useRef(null);
  const [tagDropdownUp, setTagDropdownUp] = useState(false);
  /* Category mapped by user — filter disabled
  const [mappedUserSearch, setMappedUserSearch] = useState('');
  const [mappedUserSuggestions, setMappedUserSuggestions] = useState([]);
  const [mappedUserSearching, setMappedUserSearching] = useState(false);
  const [showMappedUserDropdown, setShowMappedUserDropdown] = useState(false);
  const mappedUserBoxRef = useRef(null);
  const mappedUserSearchTimer = useRef(null);
  const [mappedDropdownUp, setMappedDropdownUp] = useState(false);
  */

  const batchBoxRef = useRef(null);
  const batchFetchTimer = useRef(null);
  const showBatchDropdownRef = useRef(false);
  const [showBatchDropdown, setShowBatchDropdown] = useState(false);
  const [batchSuggestions, setBatchSuggestions] = useState([]);
  const [batchListLoading, setBatchListLoading] = useState(false);
  const [batchLoadingMore, setBatchLoadingMore] = useState(false);
  const [batchHasMore, setBatchHasMore] = useState(false);
  const [batchDropdownUp, setBatchDropdownUp] = useState(false);

  const getBatchSearchToken = (value) => {
    const parts = String(value || '').split(',');
    return parts[parts.length - 1].trim();
  };

  const updateDropdownDirection = useCallback((boxRef, setDirection) => {
    const rect = boxRef?.current?.getBoundingClientRect?.();
    if (!rect) return;
    // If space below is small, open upward to avoid clipping near viewport bottom.
    const spaceBelow = window.innerHeight - rect.bottom;
    setDirection(spaceBelow < 320);
  }, []);

  const loadBatchSuggestions = useCallback(async (search = '', offset = 0, append = false) => {
    const q = String(search || '').trim();
    try {
      if (!append) setBatchListLoading(true);
      else setBatchLoadingMore(true);
      const res = await ProductExportService.searchSkuUploadBatches(q, PE_BATCH_PAGE_SIZE, offset);
      if (res?.status?.code === 200) {
        const batches = Array.isArray(res.data?.batches) ? res.data.batches : [];
        const hasMore = Boolean(res.data?.hasMore);
        setBatchSuggestions((prev) => (append ? [...prev, ...batches] : batches));
        setBatchHasMore(hasMore);
      } else {
        if (!append) setBatchSuggestions([]);
        setBatchHasMore(false);
      }
    } catch (e) {
      if (!append) setBatchSuggestions([]);
      setBatchHasMore(false);
    } finally {
      setBatchListLoading(false);
      setBatchLoadingMore(false);
    }
  }, []);

  const loadMoreBatchSuggestions = () => {
    if (!batchHasMore || batchListLoading || batchLoadingMore) return;
    const q = getBatchSearchToken(filters.batchIds);
    loadBatchSuggestions(q, batchSuggestions.length, true);
  };

  const handleFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const hasValue = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return value;
    return String(value ?? '').trim() !== '';
  };

  const renderFilterLabel = (label, active) => (
    <span className="pe-label-inline">
      <span>{label}</span>
      {active ? <FaCheckCircle className="pe-label-check" /> : null}
    </span>
  );

  const exportBusy =
    exportStarting ||
    exportJobStatus === 'pending' ||
    exportJobStatus === 'processing' ||
    autoDownloadBusy;

  const {
    jobHistoryTab,
    setJobHistoryTab,
    bulkSubscribeBusy,
    handleStartBulkSubscribe,
    fetchSubscribeJobs,
    clearBulkSubUiState,
    resetBulkSubscribeUi,
    bulkSubJobStatus,
    bulkSubProcessed,
    bulkSubTotalProducts,
    picker: bulkSubscribePicker,
    history: subscribeHistory,
    bulkSubscribeConfirm,
  } = useBulkSubscribe({
    filters,
    countStats,
    exportBusy,
    setErrorMessage,
    updateDropdownDirection,
    buildExportJobSnapshotFilterLines,
    formatExportJobDateTime,
    formatExportDurationSec,
  });

  const handleTagSearch = async () => {
    const q = (tagSearch || '').trim();
    if (q.length < 3) {
      setTagSuggestions([]);
      setShowTagDropdown(false);
      setTagSearching(false);
      setTagLoadingMore(false);
      return;
    }
    try {
      setTagSearching(true);
      setTagLoadingMore(false);
      const res = await ProductExportService.searchTags(q, TAG_SUGGESTIONS_PAGE_SIZE, 0);
      if (res?.status?.code === 200) {
        setTagSuggestions(res.data?.tags || []);
        setTagTotalCount(res.data?.total ?? (res.data?.tags || []).length);
      } else {
        setTagSuggestions([]);
        setTagTotalCount(0);
      }
    } catch (e) {
      setTagSuggestions([]);
      setTagTotalCount(0);
    } finally {
      setTagSearching(false);
      setShowTagDropdown(true);
    }
  };

  const handleTagShowMore = async () => {
    const q = (tagSearch || '').trim();
    if (!q || tagSearching || tagLoadingMore || tagSuggestions.length >= tagTotalCount) return;
    try {
      setTagLoadingMore(true);
      const res = await ProductExportService.searchTags(q, TAG_SUGGESTIONS_PAGE_SIZE, tagSuggestions.length);
      if (res?.status?.code === 200) {
        const nextTags = res.data?.tags || [];
        setTagSuggestions((prev) => {
          const seen = new Set(prev.map(String));
          const merged = [...prev];
          for (const t of nextTags) {
            const s = String(t);
            if (!seen.has(s)) {
              seen.add(s);
              merged.push(t);
            }
          }
          return merged;
        });
        setTagTotalCount(res.data?.total ?? tagTotalCount);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const el = tagGridScrollRef.current;
            if (el && typeof el.scrollTo === 'function') {
              el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            }
          });
        });
      }
    } finally {
      setTagLoadingMore(false);
    }
  };

  /* Category mapped by user — disabled
  const handleMappedUserSearch = async () => {
    const q = (mappedUserSearch || '').trim();
    if (q.length < 3) {
      setMappedUserSuggestions([]);
      setShowMappedUserDropdown(false);
      setMappedUserSearching(false);
      return;
    }
    try {
      setMappedUserSearching(true);
      const res = await ProductExportService.searchMappedUsers(q, 20);
      if (res?.status?.code === 200) {
        setMappedUserSuggestions(res.data?.users || []);
      } else {
        setMappedUserSuggestions([]);
      }
    } catch (e) {
      setMappedUserSuggestions([]);
    } finally {
      setMappedUserSearching(false);
      setShowMappedUserDropdown(true);
    }
  };
  */

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (tagBoxRef.current && !tagBoxRef.current.contains(e.target)) {
        setShowTagDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Auto-search tags after 3+ chars (debounced)
  useEffect(() => {
    if (tagSearchTimer.current) clearTimeout(tagSearchTimer.current);
    const q = (tagSearch || '').trim();
    if (q.length < 3) {
      setTagSuggestions([]);
      setShowTagDropdown(false);
      setTagSearching(false);
      setTagLoadingMore(false);
      return undefined;
    }
    tagSearchTimer.current = setTimeout(() => {
      handleTagSearch();
    }, 250);
    return () => {
      if (tagSearchTimer.current) clearTimeout(tagSearchTimer.current);
    };
  }, [tagSearch]);

  // Close mapped-user dropdown outside click
   /* Category mapped by user — disabled
  useEffect(() => {
    const onClick = (e) => {
      if (mappedUserBoxRef.current && !mappedUserBoxRef.current.contains(e.target)) {
        setShowMappedUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Auto-search mapped users after 3+ chars (debounced)
  useEffect(() => {
    if (mappedUserSearchTimer.current) clearTimeout(mappedUserSearchTimer.current);
    const q = (mappedUserSearch || '').trim();
    if (q.length < 3) {
      setMappedUserSuggestions([]);
      setShowMappedUserDropdown(false);
      setMappedUserSearching(false);
      return undefined;
    }
    mappedUserSearchTimer.current = setTimeout(() => {
      handleMappedUserSearch();
    }, 250);
    return () => {
      if (mappedUserSearchTimer.current) clearTimeout(mappedUserSearchTimer.current);
    };
  }, [mappedUserSearch]);
  */

  useEffect(() => {
    showBatchDropdownRef.current = showBatchDropdown;
  }, [showBatchDropdown]);

  // Debounce list refresh while typing (text after the last comma filters the API); only when dropdown is open
  useEffect(() => {
    if (!showBatchDropdownRef.current) return undefined;
    if (batchFetchTimer.current) clearTimeout(batchFetchTimer.current);
    const q = getBatchSearchToken(filters.batchIds);
    batchFetchTimer.current = setTimeout(() => {
      loadBatchSuggestions(q, 0, false);
    }, 300);
    return () => {
      if (batchFetchTimer.current) clearTimeout(batchFetchTimer.current);
    };
  }, [filters.batchIds, loadBatchSuggestions]);

  // Close batch dropdown outside click
  useEffect(() => {
    const onClick = (e) => {
      if (batchBoxRef.current && !batchBoxRef.current.contains(e.target)) {
        setShowBatchDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (!showTagDropdown) return undefined;
    const update = () => updateDropdownDirection(tagBoxRef, setTagDropdownUp);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showTagDropdown, updateDropdownDirection]);

  /* Category mapped by user — disabled
  useEffect(() => {
    if (!showMappedUserDropdown) return undefined;
    const update = () => updateDropdownDirection(mappedUserBoxRef, setMappedDropdownUp);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showMappedUserDropdown, updateDropdownDirection]);
  */

  useEffect(() => {
    if (!showBatchDropdown) return undefined;
    const update = () => updateDropdownDirection(batchBoxRef, setBatchDropdownUp);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showBatchDropdown, updateDropdownDirection]);

  /* Category mapped by user — disabled
  const selectMappedUser = (user) => {
    handleFilterChange('mappedByUserId', user._id);
    setMappedUserSearch(
      `${[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || user.uniqueId || user._id}`
    );
    setShowMappedUserDropdown(false);
  };

  const clearMappedUser = () => {
    handleFilterChange('mappedByUserId', '');
    setMappedUserSearch('');
  };
  */

  const selectBatchId = (batchId) => {
    if (!batchId) return;
    setFilters((prev) => {
      const parts = String(prev.batchIds || '')
        .split(/[,\n]/)
        .map((t) => t.trim())
        .filter(Boolean);
      if (parts.includes(batchId)) return prev;
      return { ...prev, batchIds: [...parts, batchId].join(', ') };
    });
    setShowBatchDropdown(false);
  };

  const formatBatchDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return '';
    }
  };

  const addTag = (tag) => {
    if (!tag) return;
    setFilters((prev) => {
      if (prev.tags.includes(tag)) return prev;
      return { ...prev, tags: [...prev.tags, tag] };
    });
  };

  const removeTag = (tag) => {
    setFilters((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const addAllVisibleTags = () => {
    setFilters((prev) => {
      const existing = new Set(prev.tags);
      tagSuggestions.forEach((t) => existing.add(t));
      return { ...prev, tags: Array.from(existing) };
    });
  };

  const stopExportPoll = useCallback(() => {
    if (exportPollRef.current) {
      clearInterval(exportPollRef.current);
      exportPollRef.current = null;
    }
  }, []);

  const handleHistoryExportDownload = useCallback(
    async (jobId, fileName) => {
      const id = String(jobId || '').trim();
      if (!id) return;
      setErrorMessage('');
      setManualDownloadingJobId(id);
      try {
        await downloadExportZipFile(id, fileName);
      } catch (_) {
        /* message set in downloadExportZipFile */
      } finally {
        setManualDownloadingJobId(null);
      }
    },
    [downloadExportZipFile],
  );

  const clearExportUiState = useCallback(() => {
    sessionStorage.removeItem(EXPORT_JOB_STORAGE_KEY);
    stopExportPoll();
    setExportJobId(null);
    setExportJobStatus('idle');
    setExportProcessed(0);
    setExportTotalProducts(null);
    setExportTotalChunks(null);
    setExportFilePartNumber(null);
    setExportProgressEvery(1000);
    setExportStarting(false);
    setAutoDownloadBusy(false);
    autoDownloadKickRef.current = null;
  }, [stopExportPoll]);

  const cancelActiveExportJob = useCallback(
    async (jobIdStr) => {
      const id = String(jobIdStr || '').trim();
      if (!id) return;
      setRemovingJobId(id);
      setErrorMessage('');
      try {
        const res = await ProductExportService.cancelExportJob(id);
        if (res?.status?.code !== 200) {
          setErrorMessage(res?.status?.message || 'Could not cancel this export');
          return;
        }
        await fetchExportJobs();
        await fetchSubscribeJobs();
        const saved = sessionStorage.getItem(EXPORT_JOB_STORAGE_KEY);
        if (saved && String(saved) === id) {
          clearExportUiState();
        }
        const subSaved = sessionStorage.getItem(BULK_SUBSCRIBE_JOB_STORAGE_KEY);
        if (subSaved && String(subSaved) === id) {
          clearBulkSubUiState();
        }
      } catch (err) {
        const msg = err?.data?.status?.message || err?.message || 'Could not cancel this export';
        setErrorMessage(msg);
      } finally {
        setRemovingJobId(null);
      }
    },
    [clearExportUiState, clearBulkSubUiState, fetchExportJobs, fetchSubscribeJobs],
  );

  const removeStoredExportFile = useCallback(
    async (jobIdStr) => {
      const id = String(jobIdStr || '').trim();
      if (!id) return;
      if (
        !window.confirm(
          'Remove this export ZIP from storage (S3)? You will not be able to download it again unless you start a new export.',
        )
      ) {
        return;
      }
      setDeletingStoredExportJobId(id);
      setErrorMessage('');
      try {
        const res = await ProductExportService.removeExportJobFile(id);
        if (res?.status?.code !== 200) {
          setErrorMessage(res?.status?.message || 'Could not remove the export file');
          return;
        }
        await fetchExportJobs();
        const saved = sessionStorage.getItem(EXPORT_JOB_STORAGE_KEY);
        if (saved && String(saved) === id) {
          fetchExportJobStatusRef.current?.(id);
        }
      } catch (err) {
        const msg = err?.data?.status?.message || err?.message || 'Could not remove the export file';
        setErrorMessage(msg);
      } finally {
        setDeletingStoredExportJobId(null);
      }
    },
    [fetchExportJobs],
  );

  const fetchExportJobStatus = useCallback(async (id) => {
    try {
      const res = await ProductExportService.getExportJobStatus(id);
      if (res?.status?.code !== 200 || !res.data) return;
      const d = res.data;
      setExportJobStatus(d.status || 'idle');
      setExportProcessed(Number(d.processedProducts) || 0);
      if (d.totalProducts != null && `${d.totalProducts}` !== '') {
        const tn = Number(d.totalProducts);
        if (!Number.isNaN(tn)) setExportTotalProducts(tn);
      }
      if (d.totalChunks != null && `${d.totalChunks}` !== '') {
        const tc = Number(d.totalChunks);
        if (!Number.isNaN(tc)) setExportTotalChunks(tc);
      } else {
        setExportTotalChunks(1);
      }
      if (d.filePartNumber != null && `${d.filePartNumber}` !== '') {
        const fp = Number(d.filePartNumber);
        if (!Number.isNaN(fp)) setExportFilePartNumber(fp);
      }
      if (d.progressUpdateEvery != null && Number(d.progressUpdateEvery) > 0) {
        setExportProgressEvery(Number(d.progressUpdateEvery));
      }
      setExportStarting(false);
      setExportJobs((prev) => mergeExportJobsRowFromStatus(prev, id, d));
      if (d.status === 'finished') {
        stopExportPoll();
        clearExportUiState();
        fetchExportJobs();
        return;
      }
      if (d.status === 'completed') {
        setExportStarting(false);
        if (d.downloadAvailable) {
          const sid = String(id);
          if (autoDownloadKickRef.current !== sid) {
            autoDownloadKickRef.current = sid;
            stopExportPoll();
            void runCompletedExportAutoDownload(sid, d.fileName || 'products_export.zip');
          }
        }
        return;
      }
      if (d.status === 'failed') {
        stopExportPoll();
        autoDownloadKickRef.current = null;
        setExportStarting(false);
        setExportTotalProducts(null);
        setExportTotalChunks(null);
        setExportFilePartNumber(null);
        setExportProgressEvery(1000);
        sessionStorage.removeItem(EXPORT_JOB_STORAGE_KEY);
        setExportJobId(null);
        setExportProcessed(0);
        setExportJobStatus('idle');
        setErrorMessage(d.errorMessage || 'Export failed');
        return;
      }
      if (d.status === 'cancelled') {
        stopExportPoll();
        autoDownloadKickRef.current = null;
        setExportStarting(false);
        setExportTotalProducts(null);
        setExportTotalChunks(null);
        setExportFilePartNumber(null);
        setExportProgressEvery(1000);
        sessionStorage.removeItem(EXPORT_JOB_STORAGE_KEY);
        setExportJobId(null);
        setExportProcessed(0);
        setExportJobStatus('idle');
        fetchExportJobs();
        return;
      }
    } catch (e) {
      const gone = e?.status === 404 || e?.data?.status?.code === 404;
      if (gone) {
        stopExportPoll();
        autoDownloadKickRef.current = null;
        sessionStorage.removeItem(EXPORT_JOB_STORAGE_KEY);
        setExportJobId(null);
        setExportJobStatus('idle');
        setExportProcessed(0);
        setExportTotalProducts(null);
        setExportTotalChunks(null);
        setExportFilePartNumber(null);
        setExportProgressEvery(1000);
        setExportStarting(false);
        fetchExportJobs();
      }
    }
  }, [stopExportPoll, fetchExportJobs, clearExportUiState, runCompletedExportAutoDownload]);

  useEffect(() => {
    fetchExportJobStatusRef.current = fetchExportJobStatus;
  }, [fetchExportJobStatus]);

  const startExportPolling = useCallback((id) => {
    stopExportPoll();
    fetchExportJobStatus(id);
    // Progress is persisted every 1000 products on server; poll a few seconds to avoid redundant calls.
    exportPollRef.current = setInterval(() => fetchExportJobStatus(id), 5000);
  }, [stopExportPoll, fetchExportJobStatus]);

  useEffect(() => {
    fetchExportJobs();
  }, [fetchExportJobs]);

  /** Load field catalog once so job snapshot chips can show CSV headers, not raw keys, before opening Export columns. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ProductExportService.getExportFieldCatalog();
        if (cancelled || res?.status?.code !== 200 || !res.data) return;
        setExportFieldCatalog((prev) => prev ?? res.data);
      } catch {
        /* Catalog still loads when the user opens Show Export Fields */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** If the catalog was not ready yet (e.g. prefetch still in flight), load it when opening the job snapshot so chips show labels. */
  useEffect(() => {
    if (!exportJobFilterModalJob || exportFieldCatalog) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await ProductExportService.getExportFieldCatalog();
        if (cancelled || res?.status?.code !== 200 || !res.data) return;
        setExportFieldCatalog(res.data);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exportJobFilterModalJob, exportFieldCatalog]);

  useEffect(() => {
    const saved = sessionStorage.getItem(EXPORT_JOB_STORAGE_KEY);
    if (saved) {
      setExportJobId(saved);
      setExportStarting(true);
      startExportPolling(saved);
    }
    return () => stopExportPoll();
  }, [startExportPolling, stopExportPoll]);

  const applyDefaultExportFieldSelection = useCallback((catalog) => {
    if (!catalog?.defaultProductKeys) return;
    const p = {};
    catalog.defaultProductKeys.forEach((k) => {
      p[k] = true;
    });
    const v = {};
    (catalog.defaultVariantKeys || []).forEach((k) => {
      v[k] = true;
    });
    setProductFieldChecked(p);
    setVariantFieldChecked(v);
  }, []);

  const openExportFieldsModal = useCallback(async () => {
    setExportFieldsOpen(true);
    setErrorMessage('');
    setExportFieldCatalogLoading(true);
    try {
      const res = await ProductExportService.getExportFieldCatalog();
      if (res?.status?.code === 200 && res.data) {
        const catalog = res.data;
        setExportFieldCatalog(catalog);
        setProductFieldChecked((prevP) => {
          const next = {};
          for (const g of catalog.productGroups || []) {
            for (const f of g.fields || []) {
              if (!f?.key) continue;
              next[f.key] = Object.prototype.hasOwnProperty.call(prevP, f.key) ? prevP[f.key] : true;
            }
          }
          return next;
        });
        setVariantFieldChecked((prevV) => {
          const next = {};
          for (const g of catalog.variantGroups || []) {
            for (const f of g.fields || []) {
              if (!f?.key) continue;
              next[f.key] = Object.prototype.hasOwnProperty.call(prevV, f.key) ? prevV[f.key] : true;
            }
          }
          return next;
        });
      } else {
        setErrorMessage(res?.status?.message || 'Failed to load export fields');
      }
    } catch (e) {
      setErrorMessage(e?.data?.status?.message || e?.message || 'Failed to load export fields');
    } finally {
      setExportFieldCatalogLoading(false);
    }
  }, []);

  const closeExportFieldsModal = useCallback(() => {
    setExportFieldsOpen(false);
  }, []);

  const toggleProductExportField = useCallback((key) => {
    setProductFieldChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleVariantExportField = useCallback((key) => {
    setVariantFieldChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const uncheckProductExportField = useCallback((key) => {
    setProductFieldChecked((prev) => ({ ...prev, [key]: false }));
  }, []);

  const uncheckVariantExportField = useCallback((key) => {
    setVariantFieldChecked((prev) => ({ ...prev, [key]: false }));
  }, []);

  const selectAllExportFields = useCallback(() => {
    if (exportFieldCatalog) applyDefaultExportFieldSelection(exportFieldCatalog);
  }, [exportFieldCatalog, applyDefaultExportFieldSelection]);

  const unselectAllExportFields = useCallback(() => {
    if (!exportFieldCatalog) return;
    const p = {};
    const v = {};
    for (const g of exportFieldCatalog.productGroups || []) {
      for (const f of g.fields || []) {
        if (f?.key) p[f.key] = false;
      }
    }
    for (const g of exportFieldCatalog.variantGroups || []) {
      for (const f of g.fields || []) {
        if (f?.key) v[f.key] = false;
      }
    }
    setProductFieldChecked(p);
    setVariantFieldChecked(v);
  }, [exportFieldCatalog]);

  useEffect(() => {
    if (!exportFieldsOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeExportFieldsModal();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [exportFieldsOpen, closeExportFieldsModal]);

  const handleStartExport = async () => {
    try {
      setErrorMessage('');
      if (bulkSubscribeBusy) {
        setErrorMessage('Cancel or wait for the bulk subscribe job before exporting.');
        return;
      }

      const hasFieldMaps =
        Object.keys(productFieldChecked).length > 0 || Object.keys(variantFieldChecked).length > 0;
      const nProductOn = Object.values(productFieldChecked).filter(Boolean).length;
      const nVariantOn = Object.values(variantFieldChecked).filter(Boolean).length;
      if (hasFieldMaps && nProductOn === 0 && nVariantOn === 0) {
        setErrorMessage(
          'Select at least one export column before exporting. Open Show Export Fields and turn columns on, or click Select all there.',
        );
        return;
      }

      setExportStarting(true);
      setExportJobStatus('pending');
      setExportProcessed(0);
      setExportTotalProducts(null);
      setExportTotalChunks(null);
      setExportFilePartNumber(null);
      autoDownloadKickRef.current = null;
      setAutoDownloadBusy(false);

      const hasFieldSelection =
        Object.keys(productFieldChecked).length > 0 || Object.keys(variantFieldChecked).length > 0;
      let exportFieldKeys = null;
      if (hasFieldSelection) {
        const productKeys = Object.entries(productFieldChecked)
          .filter(([, on]) => on)
          .map(([k]) => k);
        const variantKeys = Object.entries(variantFieldChecked)
          .filter(([, on]) => on)
          .map(([k]) => k);
        exportFieldKeys = {
          productKeys: productKeys.length ? productKeys : undefined,
          variantKeys,
        };
      }

      const res = await ProductExportService.startExportJob(filters, exportFieldKeys);
      if (res?.status?.code !== 200 || !res.data?.exportJobId) {
        setExportStarting(false);
        setExportJobStatus('idle');
        setErrorMessage(res?.status?.message || 'Failed to start export');
        return;
      }
      const id = String(res.data.exportJobId);
      if (res.data?.totalProducts != null && res.data.totalProducts !== '') {
        setExportTotalProducts(Number(res.data.totalProducts));
      }
      if (res.data?.totalChunks != null && `${res.data.totalChunks}` !== '') {
        const tc = Number(res.data.totalChunks);
        setExportTotalChunks(Number.isFinite(tc) ? tc : 1);
      }
      sessionStorage.setItem(EXPORT_JOB_STORAGE_KEY, id);
      setExportJobId(id);
      startExportPolling(id);
      fetchExportJobs();
    } catch (error) {
      setExportStarting(false);
      setExportJobStatus('idle');
      setExportTotalProducts(null);
      setExportTotalChunks(null);
      setExportFilePartNumber(null);
      const conflict = error?.status === 409 || error?.data?.status?.code === 409;
      const apiMsg = error?.data?.status?.message;
      const msg = conflict
        ? apiMsg ||
          error?.message ||
          'Another export or subscribe job is already running. Wait for it to finish or cancel it.'
        : apiMsg || error?.message || 'Failed to start export';
      setErrorMessage(msg);
    }
  };

  const runningProgressFor = (job) => {
    const base = Number(job.processedProducts) || 0;
    if (String(job.exportJobId) === String(exportJobId)) {
      return Math.max(base, Number(exportProcessed) || 0);
    }
    return base;
  };

  const handleGetCount = async () => {
    try {
      setCounting(true);
      setErrorMessage('');
      const res = await ProductExportService.getCount(filters);
      if (res?.status?.code === 200) {
        setCountStats({
          productCount: res.data?.productCount ?? res.data?.count ?? 0,
          variantCount: res.data?.variantCount ?? 0,
        });
      } else {
        setErrorMessage(res?.status?.message || 'Failed to get count');
      }
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to get count');
    } finally {
      setCounting(false);
    }
  };

  const resetAll = () => {
    setErrorMessage('');
    setFilters(initialFilters);
    setCountStats(null);
    setTagSearch('');
    setTagSuggestions([]);
    setTagTotalCount(0);
    setShowTagDropdown(false);
    // mapped-user filter disabled:
    // setMappedUserSearch('');
    // setMappedUserSuggestions([]);
    // setShowMappedUserDropdown(false);
    setShowBatchDropdown(false);
    setBatchSuggestions([]);
    setBatchHasMore(false);
    setBatchLoadingMore(false);
    setBatchListLoading(false);
    stopExportPoll();
    sessionStorage.removeItem(EXPORT_JOB_STORAGE_KEY);
    setExportJobId(null);
    setExportJobStatus('idle');
    setExportProcessed(0);
    setExportTotalProducts(null);
    setExportTotalChunks(null);
    setExportFilePartNumber(null);
    setExportProgressEvery(1000);
    setExportStarting(false);
    setAutoDownloadBusy(false);
    autoDownloadKickRef.current = null;
    setExportFieldsOpen(false);
    setExportFieldCatalog(null);
    setProductFieldChecked({});
    setVariantFieldChecked({});
    resetBulkSubscribeUi();
  };

  const canShowMoreTags = tagSuggestions.length < tagTotalCount;

  const activeFilterItems = [
    (hasValue(filters.imgUrlLengthFrom) || hasValue(filters.imgUrlLengthTo)) && {
      key: 'imgUrlLengthRange',
      label: (() => {
        const from = String(filters.imgUrlLengthFrom ?? '').trim();
        const to = String(filters.imgUrlLengthTo ?? '').trim();
        if (from && to) return `imgUrl length: ${from}–${to}`;
        if (from) return `imgUrl length: from ${from}`;
        return `imgUrl length: to ${to}`;
      })(),
    },
    (hasValue(filters.variationsLengthFrom) || hasValue(filters.variationsLengthTo)) && {
      key: 'variationsLengthRange',
      label: (() => {
        const from = String(filters.variationsLengthFrom ?? '').trim();
        const to = String(filters.variationsLengthTo ?? '').trim();
        if (from && to) return `Variations length: ${from}–${to}`;
        if (from) return `Variations length: from ${from}`;
        return `Variations length: to ${to}`;
      })(),
    },
    hasValue(filters.variantQualityMode) && {
      key: 'variantQualityMode',
      label: `Weight/Dimensions: ${variantQualityOptions.find((o) => o.value === filters.variantQualityMode)?.label || filters.variantQualityMode}`,
    },
    hasValue(filters.status) && { key: 'status', label: `Status: ${filters.status === '5' ? 'List' : filters.status === '6' ? 'Draft' : filters.status}` },
    hasValue(filters.isWhiteImageAvailable) && { key: 'isWhiteImageAvailable', label: `White image: ${filters.isWhiteImageAvailable === 'true' ? 'Yes' : filters.isWhiteImageAvailable === 'false' ? 'No' : filters.isWhiteImageAvailable}` },
    hasValue(filters.isDropshippingAvailable) && { key: 'isDropshippingAvailable', label: `Dropshipping: ${filters.isDropshippingAvailable === 'true' ? 'Yes' : filters.isDropshippingAvailable === 'false' ? 'No' : filters.isDropshippingAvailable}` },
    hasValue(filters.batchIds) && { key: 'batchIds', label: `Batch ID(s): ${filters.batchIds}` },
    (hasValue(filters.moqFrom) || hasValue(filters.moqTo)) && {
      key: 'moqRange',
      label: (() => {
        const from = String(filters.moqFrom ?? '').trim();
        const to = String(filters.moqTo ?? '').trim();
        if (from && to) return `MOQ: ${from}–${to}`;
        if (from) return `MOQ: from ${from}`;
        return `MOQ: to ${to}`;
      })(),
    },
    hasValue(filters.skus) && { key: 'skus', label: `SKU(s): ${filters.skus}` },
    (hasValue(filters.createdAtFrom) || hasValue(filters.createdAtTo)) && { key: 'createdAtRange', label: `Created: ${filters.createdAtFrom || '...'} to ${filters.createdAtTo || '...'}` },
    (hasValue(filters.updatedAtFrom) || hasValue(filters.updatedAtTo)) && { key: 'updatedAtRange', label: `Updated: ${filters.updatedAtFrom || '...'} to ${filters.updatedAtTo || '...'}` },
    hasValue(filters.categoryIds) && { key: 'categoryIds', label: `Category IDs: ${filters.categoryIds}` },
    hasValue(filters.topCategoryIds) && { key: 'topCategoryIds', label: `Top Category IDs: ${filters.topCategoryIds}` },
    hasValue(filters.secondCategoryIds) && { key: 'secondCategoryIds', label: `Second Category IDs: ${filters.secondCategoryIds}` },
    hasValue(filters.thirdCategoryIds) && { key: 'thirdCategoryIds', label: `Third Category IDs: ${filters.thirdCategoryIds}` },
    // hasValue(filters.mappedByUserId) && { key: 'mappedByUserId', label: `Mapped User: ${mappedUserSearch || filters.mappedByUserId}` },
    filters.categoryIdRequired && { key: 'categoryIdRequired', label: 'Category ID required' },
    filters.descriptionRequired && { key: 'descriptionRequired', label: 'Description required' },
    filters.imgDescriptionRequired && { key: 'imgDescriptionRequired', label: 'Image description required' },
    filters.keywordSearchRequired && { key: 'keywordSearchRequired', label: 'Search keyword required' },
    filters.moq1Required && { key: 'moq1Required', label: 'MOQ 1 required' },
  ].filter(Boolean);

  const exportFieldHeaderByKey = useMemo(() => {
    if (!exportFieldCatalog) return {};
    const m = {};
    for (const g of exportFieldCatalog.productGroups || []) {
      for (const f of g.fields || []) {
        if (f?.key) m[f.key] = f.header || f.key;
      }
    }
    for (const g of exportFieldCatalog.variantGroups || []) {
      for (const f of g.fields || []) {
        if (f?.key) m[f.key] = f.header || f.key;
      }
    }
    return m;
  }, [exportFieldCatalog]);

  const selectedExportProductItems = useMemo(
    () =>
      Object.entries(productFieldChecked)
        .filter(([, on]) => on)
        .map(([key]) => ({ key, label: exportFieldHeaderByKey[key] || key }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [productFieldChecked, exportFieldHeaderByKey],
  );

  const selectedExportVariantItems = useMemo(
    () =>
      Object.entries(variantFieldChecked)
        .filter(([, on]) => on)
        .map(([key]) => ({ key, label: exportFieldHeaderByKey[key] || key }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [variantFieldChecked, exportFieldHeaderByKey],
  );

  const exportJobModalFilterLines = useMemo(
    () =>
      exportJobFilterModalJob ? buildExportJobSnapshotFilterLines(exportJobFilterModalJob.filters) : [],
    [exportJobFilterModalJob],
  );

  const exportJobModalColumns = useMemo(() => {
    if (!exportJobFilterModalJob) {
      return { product: [], variant: [], allFieldsDefault: true };
    }
    const ek = exportJobFilterModalJob.exportFieldKeys;
    const pk = Array.isArray(ek?.productKeys) ? ek.productKeys : [];
    const vk = Array.isArray(ek?.variantKeys) ? ek.variantKeys : [];
    if (pk.length === 0 && vk.length === 0) {
      return { product: [], variant: [], allFieldsDefault: true };
    }
    const h = exportFieldHeaderByKey;
    const toItems = (keys) =>
      keys
        .map((k) => ({ key: k, label: h[k] || k }))
        .sort((a, b) => a.label.localeCompare(b.label));
    return {
      product: toItems(pk),
      variant: toItems(vk),
      allFieldsDefault: false,
    };
  }, [exportJobFilterModalJob, exportFieldHeaderByKey]);


  const showExportSelectionSummary =
    Boolean(exportFieldCatalog) &&
    (Object.keys(productFieldChecked).length > 0 || Object.keys(variantFieldChecked).length > 0);

  const clearActiveFilter = (itemKey) => {
    switch (itemKey) {
      case 'imgUrlLengthRange':
        setFilters((prev) => ({ ...prev, imgUrlLengthFrom: '', imgUrlLengthTo: '' }));
        return;
      case 'variationsLengthRange':
        setFilters((prev) => ({ ...prev, variationsLengthFrom: '', variationsLengthTo: '' }));
        return;
      case 'variantQualityMode':
      case 'status':
      case 'isWhiteImageAvailable':
      case 'isDropshippingAvailable':
      case 'batchIds':
      case 'skus':
        handleFilterChange(itemKey, '');
        return;
      case 'moqRange':
        setFilters((prev) => ({ ...prev, moqFrom: '', moqTo: '' }));
        return;
      case 'categoryIds':
      case 'topCategoryIds':
      case 'secondCategoryIds':
      case 'thirdCategoryIds':
        handleFilterChange(itemKey, '');
        return;
      // case 'mappedByUserId':
      //   handleFilterChange(itemKey, '');
      //   setMappedUserSearch('');
      //   return;
      case 'createdAtRange':
        setFilters((prev) => ({ ...prev, createdAtFrom: '', createdAtTo: '' }));
        return;
      case 'updatedAtRange':
        setFilters((prev) => ({ ...prev, updatedAtFrom: '', updatedAtTo: '' }));
        return;
      case 'categoryIdRequired':
      case 'descriptionRequired':
      case 'imgDescriptionRequired':
      case 'keywordSearchRequired':
      case 'moq1Required':
        handleFilterChange(itemKey, false);
        return;
      default:
        return;
    }
  };

  return (
    <div className="pe-container">
      <div className="pe-header">
        <h2>Product Export</h2>
        <p className="pe-head-sub">
          Filter the catalog (same criteria as exports), optionally run CSV exports (ZIP via S3), or bulk subscribe
          matched parent products for selected users — large runs process in background jobs listed below.
        </p>
      </div>

      <div className="pe-card">

            <h4 className="pe-subhead"><FaFilter /> Product Filters</h4>
            <div className="pe-grid">
          <div
            className={`pe-moq-range${hasValue(filters.imgUrlLengthFrom) || hasValue(filters.imgUrlLengthTo) ? ' pe-filter-active' : ''}`}
          >
            {renderFilterLabel(
              'imgUrl length',
              hasValue(filters.imgUrlLengthFrom) || hasValue(filters.imgUrlLengthTo),
            )}
            <div className="pe-moq-range-fields">
              <input
                type="number"
                min="0"
                step="1"
                placeholder="From"
                value={filters.imgUrlLengthFrom}
                onChange={(e) => handleFilterChange('imgUrlLengthFrom', e.target.value)}
                autoComplete="off"
              />
              <span className="pe-moq-range-sep">–</span>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="To"
                value={filters.imgUrlLengthTo}
                onChange={(e) => handleFilterChange('imgUrlLengthTo', e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          <div
            className={`pe-moq-range${hasValue(filters.variationsLengthFrom) || hasValue(filters.variationsLengthTo) ? ' pe-filter-active' : ''}`}
          >
            {renderFilterLabel(
              'Variations length',
              hasValue(filters.variationsLengthFrom) || hasValue(filters.variationsLengthTo),
            )}
            <div className="pe-moq-range-fields">
              <input
                type="number"
                min="0"
                step="1"
                placeholder="From"
                value={filters.variationsLengthFrom}
                onChange={(e) => handleFilterChange('variationsLengthFrom', e.target.value)}
                autoComplete="off"
              />
              <span className="pe-moq-range-sep">–</span>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="To"
                value={filters.variationsLengthTo}
                onChange={(e) => handleFilterChange('variationsLengthTo', e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          <label className={hasValue(filters.status) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('Status', hasValue(filters.status))}
            <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
              {statusOptions.map((s) => <option key={s.label} value={s.value}>{s.label}</option>)}
            </select>
          </label>

          <label className={hasValue(filters.isWhiteImageAvailable) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('White image available', hasValue(filters.isWhiteImageAvailable))}
            <select value={filters.isWhiteImageAvailable} onChange={(e) => handleFilterChange('isWhiteImageAvailable', e.target.value)}>
              {whiteImageOptions.map((o) => <option key={o.label} value={o.value}>{o.label}</option>)}
            </select>
          </label>

          <label className={hasValue(filters.isDropshippingAvailable) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('Dropshipping available', hasValue(filters.isDropshippingAvailable))}
            <select value={filters.isDropshippingAvailable} onChange={(e) => handleFilterChange('isDropshippingAvailable', e.target.value)}>
              {dropshippingAvailableOptions.map((o) => <option key={o.label} value={o.value}>{o.label}</option>)}
            </select>
          </label>

          <div className="pe-batch-picker" ref={batchBoxRef}>
            <label className={hasValue(filters.batchIds) ? 'pe-filter-active' : ''}>
              {renderFilterLabel('Batch ID(s)', hasValue(filters.batchIds))}
              <div className="pe-mapped-user-input-wrap">
                <input
                  type="text"
                  placeholder="Click to list SKU upload batches, or paste IDs"
                  value={filters.batchIds}
                  onChange={(e) => handleFilterChange('batchIds', e.target.value)}
                  onFocus={() => {
                    setShowBatchDropdown(true);
                    const q = getBatchSearchToken(filters.batchIds);
                    loadBatchSuggestions(q, 0, false);
                  }}
                  autoComplete="off"
                />
              </div>
            </label>
            {showBatchDropdown && (
              <div className={`pe-tag-dropdown pe-batch-dropdown${batchDropdownUp ? ' pe-tag-dropdown-up' : ''}`}>
                <div className="pe-batch-dropdown-head">
                  <span>SKU upload jobs (most recent)</span>
                  <button
                    type="button"
                    className="pe-btn-secondary pe-btn-tiny"
                    onClick={() => {
                      const q = getBatchSearchToken(filters.batchIds);
                      loadBatchSuggestions(q, 0, false);
                    }}
                  >
                    Refresh
                  </button>
                </div>
                {batchListLoading && batchSuggestions.length === 0 ? (
                  <div className="pe-dropdown-loading">Loading…</div>
                ) : batchSuggestions.length === 0 ? (
                  <div className="pe-dropdown-empty">No batches found.</div>
                ) : (
                  <>
                    <ul className="pe-batch-list">
                      {batchSuggestions.map((b) => (
                        <li key={b.batchId}>
                          <button
                            type="button"
                            className="pe-batch-row"
                            onClick={() => selectBatchId(b.batchId)}
                          >
                            <span className="pe-batch-id pe-mono">{b.batchId}</span>
                            <span className="pe-batch-meta">
                              {[b.jobType, b.status].filter(Boolean).join(' · ')}
                              {b.startedAt ? ` · ${formatBatchDate(b.startedAt)}` : ''}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    {batchHasMore ? (
                      <div className="pe-batch-dropdown-footer">
                        <button
                          type="button"
                          className="pe-btn-secondary pe-btn-tiny pe-batch-show-more"
                          disabled={batchLoadingMore || batchListLoading}
                          onClick={loadMoreBatchSuggestions}
                        >
                          {batchLoadingMore ? 'Loading…' : 'Show more'}
                        </button>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            )}
          </div>

          <div
            className={`pe-moq-range${hasValue(filters.moqFrom) || hasValue(filters.moqTo) ? ' pe-filter-active' : ''}`}
          >
            {renderFilterLabel(
              'MOQ range',
              hasValue(filters.moqFrom) || hasValue(filters.moqTo),
            )}
            <div className="pe-moq-range-fields">
              <input
                type="number"
                min="0"
                step="1"
                placeholder="From"
                value={filters.moqFrom}
                onChange={(e) => handleFilterChange('moqFrom', e.target.value)}
                autoComplete="off"
              />
              <span className="pe-moq-range-sep">–</span>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="To"
                value={filters.moqTo}
                onChange={(e) => handleFilterChange('moqTo', e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>

          <label className={hasValue(filters.skus) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('SKU(s)', hasValue(filters.skus))}
            <input
              type="text"
              placeholder="Enter SKU or SKU1,SKU2"
              value={filters.skus}
              onChange={(e) => handleFilterChange('skus', e.target.value)}
            />
          </label>

          <label className={hasValue(filters.variantQualityMode) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('Weight/Dimensions', hasValue(filters.variantQualityMode))}
            <select value={filters.variantQualityMode} onChange={(e) => handleFilterChange('variantQualityMode', e.target.value)}>
              {variantQualityOptions.map((o) => <option key={o.value || 'all'} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>

        <h4 className="pe-subhead"><FaCalendarAlt /> Date Range</h4>
        <div className="pe-grid pe-grid-dates">
          <label className={hasValue(filters.createdAtFrom) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('Created From', hasValue(filters.createdAtFrom))}
            <input type="date" value={filters.createdAtFrom} onChange={(e) => handleFilterChange('createdAtFrom', e.target.value)} />
          </label>

          <label className={hasValue(filters.createdAtTo) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('Created To', hasValue(filters.createdAtTo))}
            <input type="date" value={filters.createdAtTo} onChange={(e) => handleFilterChange('createdAtTo', e.target.value)} />
          </label>

          <label className={hasValue(filters.updatedAtFrom) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('Updated From', hasValue(filters.updatedAtFrom))}
            <input type="date" value={filters.updatedAtFrom} onChange={(e) => handleFilterChange('updatedAtFrom', e.target.value)} />
          </label>

          <label className={hasValue(filters.updatedAtTo) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('Updated To', hasValue(filters.updatedAtTo))}
            <input type="date" value={filters.updatedAtTo} onChange={(e) => handleFilterChange('updatedAtTo', e.target.value)} />
          </label>
        </div>

        <h4 className="pe-subhead"><FaFolderOpen /> Category Filters</h4>
        <div className="pe-grid">
          <label className={hasValue(filters.categoryIds) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('Category IDs (comma-separated)', hasValue(filters.categoryIds))}
            <input
              type="text"
              placeholder="e.g. 1033125, 1033126"
              value={filters.categoryIds}
              onChange={(e) => handleFilterChange('categoryIds', e.target.value)}
            />
          </label>
          <label className={hasValue(filters.topCategoryIds) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('China Top Category IDs', hasValue(filters.topCategoryIds))}
            <input
              type="text"
              placeholder="e.g. 122916002"
              value={filters.topCategoryIds}
              onChange={(e) => handleFilterChange('topCategoryIds', e.target.value)}
            />
          </label>
          <label className={hasValue(filters.secondCategoryIds) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('China Second Category IDs', hasValue(filters.secondCategoryIds))}
            <input
              type="text"
              placeholder="comma-separated"
              value={filters.secondCategoryIds}
              onChange={(e) => handleFilterChange('secondCategoryIds', e.target.value)}
            />
          </label>
          <label className={hasValue(filters.thirdCategoryIds) ? 'pe-filter-active' : ''}>
            {renderFilterLabel('China Third Category IDs', hasValue(filters.thirdCategoryIds))}
            <input
              type="text"
              placeholder="comma-separated"
              value={filters.thirdCategoryIds}
              onChange={(e) => handleFilterChange('thirdCategoryIds', e.target.value)}
            />
          </label>
        </div>

        <h4 className="pe-subhead"><FaTag /> Tags</h4>
        <div className="pe-tag-user-row">
          <div className={`pe-tag-picker pe-tag-user-col ${hasValue(filters.tags) ? 'pe-filter-active-box' : ''}`} ref={tagBoxRef}>
            <div className="pe-tag-user-col-title">
              {renderFilterLabel('Tag picker', hasValue(filters.tags))}
            </div>
            <div className="pe-tag-input-row">
              <input
                type="text"
                placeholder="Search tags... (e.g. watch, hair band)"
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleTagSearch();
                  }
                }}
              />
              <button
                type="button"
                className="pe-btn-secondary pe-search-btn"
                onClick={handleTagSearch}
              >
                Search
              </button>
              {showTagDropdown && tagSuggestions.length > 0 && (
                <button
                  type="button"
                  className="pe-btn-secondary pe-add-all-btn"
                  onClick={addAllVisibleTags}
                  title="Add all matching tags"
                >
                  Add all ({tagSuggestions.length})
                </button>
              )}
            </div>

            {showTagDropdown && (
              <div className={`pe-tag-dropdown${tagDropdownUp ? ' pe-tag-dropdown-up' : ''}`}>
                {tagSearching && tagSuggestions.length === 0 ? (
                  <div className="pe-tag-loading"><FaSpinner className="pe-spin" /> Searching...</div>
                ) : tagSuggestions.length === 0 && !tagSearching ? (
                  <div className="pe-tag-empty">No matching tags</div>
                ) : (
                  <div className="pe-tag-grid-wrap">
                    {tagSearching ? (
                      <div className="pe-tag-results-updating" role="status">
                        <FaSpinner className="pe-spin" aria-hidden /> Updating results…
                      </div>
                    ) : null}
                    <div className="pe-tag-grid-head">
                      <button
                        type="button"
                        className="pe-btn-secondary pe-tag-select-all"
                        onClick={addAllVisibleTags}
                        disabled={tagSearching}
                      >
                        Select All
                      </button>
                      <span className="pe-tag-grid-meta">
                        Showing {tagSuggestions.length} of {tagTotalCount} tags (similar first)
                      </span>
                    </div>
                    <div className="pe-tag-grid-scroll" ref={tagGridScrollRef}>
                      <div className="pe-tag-grid">
                        {tagSuggestions.map((tag) => {
                          const selected = filters.tags.includes(tag);
                          return (
                            <label key={tag} className={`pe-tag-grid-item${selected ? ' pe-tag-option-selected' : ''}`}>
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => (selected ? removeTag(tag) : addTag(tag))}
                              />
                              <span>{tag}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    {canShowMoreTags && (
                      <div className="pe-tag-show-more-row">
                        <button
                          type="button"
                          className="pe-btn-secondary pe-tag-show-more-btn"
                          onClick={handleTagShowMore}
                          disabled={tagSearching || tagLoadingMore}
                        >
                          {tagLoadingMore ? (
                            <>
                              <FaSpinner className="pe-spin" aria-hidden /> Loading…
                            </>
                          ) : (
                            'Show more'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {filters.tags.length > 0 && (
              <div className="pe-tag-chips">
                {filters.tags.map((tag) => (
                  <span key={tag} className="pe-tag-chip">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="pe-tag-chip-remove">
                      <FaTimes />
                    </button>
                  </span>
                ))}
                <button type="button" className="pe-tag-clear" onClick={() => handleFilterChange('tags', [])}>
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Category mapped by user filter — disabled */}
          {/* <div className={`pe-mapped-user-col ${hasValue(filters.mappedByUserId) ? 'pe-filter-active-box' : ''}`} ref={mappedUserBoxRef}>
            <div className="pe-tag-user-col-title">
              {renderFilterLabel('Category mapped by user', hasValue(filters.mappedByUserId))}
            </div>
            <div className="pe-mapped-user-input-row">
              <input
                type="text"
                placeholder="Search by name, email or unique id..."
                value={mappedUserSearch}
                onChange={(e) => {
                  setMappedUserSearch(e.target.value);
                  if (filters.mappedByUserId) handleFilterChange('mappedByUserId', '');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleMappedUserSearch();
                  }
                }}
                autoComplete="off"
              />
              <button type="button" className="pe-btn-secondary pe-search-btn" onClick={handleMappedUserSearch}>
                Search
              </button>
              {filters.mappedByUserId ? (
                <button type="button" className="pe-btn-secondary pe-mapped-user-clear" onClick={clearMappedUser}>
                  Clear
                </button>
              ) : null}
            </div>

            {filters.mappedByUserId ? (
              <p className="pe-mapped-selected-note">Filter active: products mapped by this admin user.</p>
            ) : null}

            {showMappedUserDropdown && (
              <div className={`pe-tag-dropdown pe-mapped-dropdown${mappedDropdownUp ? ' pe-tag-dropdown-up' : ''}`}>
                {mappedUserSearching ? (
                  <div className="pe-tag-loading"><FaSpinner className="pe-spin" /> Searching...</div>
                ) : mappedUserSuggestions.length === 0 ? (
                  <div className="pe-tag-empty">No users found</div>
                ) : (
                  mappedUserSuggestions.map((u) => {
                    const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
                    const secondary = [u.uniqueId, u.email].filter(Boolean).join(' · ') || String(u._id);
                    return (
                      <div key={u._id} className="pe-mapped-result-row">
                        <div className="pe-mapped-result-text">
                          <span className="pe-mapped-name">{fullName || u.email || u._id}</span>
                          <span className="pe-mapped-secondary">{secondary}</span>
                        </div>
                        <button
                          type="button"
                          className="pe-btn-secondary pe-mapped-select-btn"
                          onClick={() => selectMappedUser(u)}
                        >
                          Select
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div> */}
        </div>

        <h4 className="pe-subhead"><FaShieldAlt /> Required Field Filters</h4>
        <div className="pe-checkbox-grid">
          {[
            ['categoryIdRequired', 'Category ID required'],
            ['descriptionRequired', 'Description required'],
            ['imgDescriptionRequired', 'Image description required'],
            ['keywordSearchRequired', 'Search keyword required'],
            ['moq1Required', 'MOQ 1 required'],
          ].map(([key, label]) => (
            <label key={key} className={`pe-checkbox ${Boolean(filters[key]) ? 'pe-filter-active' : ''}`}>
              <input
                type="checkbox"
                checked={Boolean(filters[key])}
                onChange={(e) => handleFilterChange(key, e.target.checked)}
              />
              <span className="pe-label-inline">
                <span>{label}</span>
                {Boolean(filters[key]) ? <FaCheckCircle className="pe-label-check" /> : null}
              </span>
            </label>
          ))}
        </div>

        {activeFilterItems.length > 0 && (
          <div className="pe-active-filters-wrap pe-active-bottom">
            <div className="pe-active-filters-head">
              <div className="pe-active-filters-title">Active Filters ({activeFilterItems.length})</div>
              <button type="button" className="pe-inline-remove" onClick={resetAll}>Remove All</button>
            </div>
            <div className="pe-active-filters-list">
              {activeFilterItems.map((item) => (
                <span key={item.key} className="pe-active-filter-chip">
                  <span>{item.label}</span>
                  <button
                    type="button"
                    className="pe-chip-remove-btn"
                    onClick={() => clearActiveFilter(item.key)}
                    title="Remove this filter"
                    aria-label={`Remove ${item.label}`}
                  >
                    <FaTimes />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {showExportSelectionSummary ? (
          <div className="pe-export-selection-wrap">
            <div className="pe-export-selection-head">
              <div className="pe-export-selection-head-text">
                <div className="pe-export-selection-title">Selected export columns</div>
                {selectedExportVariantItems.length === 0 ? (
                  <p className="pe-export-selection-note">
                    No variant columns — the export uses one row per product.
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="pe-btn-secondary pe-export-selection-edit"
                onClick={openExportFieldsModal}
                disabled={exportBusy || bulkSubscribeBusy}
              >
                <FaColumns aria-hidden /> Change
              </button>
            </div>
            <div className="pe-export-selection-section">
              <div className="pe-export-selection-section-label">
                Product fields ({selectedExportProductItems.length})
              </div>
              <div className="pe-export-selection-list">
                {selectedExportProductItems.map(({ key, label }) => (
                  <span key={key} className="pe-export-selection-chip">
                    <span className="pe-export-selection-chip-label">{label}</span>
                    <button
                      type="button"
                      className="pe-export-selection-chip-remove"
                      onClick={() => uncheckProductExportField(key)}
                      disabled={exportBusy || bulkSubscribeBusy}
                      title="Remove from export"
                      aria-label={`Remove ${label} from export`}
                    >
                      <FaTimes aria-hidden />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div className="pe-export-selection-section">
              <div className="pe-export-selection-section-label">
                Variant fields ({selectedExportVariantItems.length})
              </div>
              <div className="pe-export-selection-list">
                {selectedExportVariantItems.length === 0 ? (
                  <span className="pe-export-selection-empty">None selected</span>
                ) : (
                  selectedExportVariantItems.map(({ key, label }) => (
                    <span key={key} className="pe-export-selection-chip">
                      <span className="pe-export-selection-chip-label">{label}</span>
                      <button
                        type="button"
                        className="pe-export-selection-chip-remove"
                        onClick={() => uncheckVariantExportField(key)}
                        disabled={exportBusy || bulkSubscribeBusy}
                        title="Remove from export"
                        aria-label={`Remove ${label} from export`}
                      >
                        <FaTimes aria-hidden />
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : null}

        <BulkSubscribeUserPicker picker={bulkSubscribePicker} />

        <div
          className="pe-actions pe-actions-modern"
          role="tablist"
          aria-label="Job history"
        >
          <button
            type="button"
            role="tab"
            id="pe-job-tab-export"
            aria-controls="pe-job-history-export"
            aria-selected={jobHistoryTab === 'export_jobs'}
            className={`pe-btn-secondary pe-job-history-tab-btn${jobHistoryTab === 'export_jobs' ? ' pe-job-history-tab-btn--active' : ''}`}
            onClick={() => setJobHistoryTab('export_jobs')}
          >
            Export jobs History
          </button>
          <button
            type="button"
            role="tab"
            id="pe-job-tab-subscribe"
            aria-controls="pe-job-history-subscribe"
            aria-selected={jobHistoryTab === 'subscription_history'}
            className={`pe-btn-secondary pe-job-history-tab-btn${jobHistoryTab === 'subscription_history' ? ' pe-job-history-tab-btn--active' : ''}`}
            onClick={() => setJobHistoryTab('subscription_history')}
          >
            Subscription history
          </button>
          <div className="pe-actions-buttons">
            <button
              type="button"
              className="pe-btn-secondary"
              onClick={openExportFieldsModal}
              disabled={exportBusy || bulkSubscribeBusy}
            >
              <FaColumns aria-hidden /> Show Export Fields
            </button>
            <button type="button" onClick={resetAll} className="pe-btn-secondary" disabled={exportBusy || bulkSubscribeBusy}>
              <FaUndoAlt /> Reset
            </button>
            <button
              type="button"
              onClick={handleGetCount}
              disabled={counting || exportBusy || bulkSubscribeBusy}
              className="pe-btn-secondary"
            >
              {counting ? <><FaSpinner className="pe-spin" /> Counting...</> : 'Get Count'}
            </button>
            <button
              type="button"
              onClick={handleStartBulkSubscribe}
              disabled={exportBusy || bulkSubscribeBusy}
              className="pe-btn-secondary pe-btn-bulk-sub"
            >
              {bulkSubscribeBusy ? (
                <>
                  <FaSpinner className="pe-spin" aria-hidden /> Subscribing…
                </>
              ) : (
                <>
                  <FaUsers aria-hidden /> Bulk subscribe selected users
                </>
              )}
            </button>
            <button type="button" onClick={handleStartExport} disabled={exportBusy || bulkSubscribeBusy} className="pe-btn-primary pe-export-main-btn">
              {exportBusy ? (
                <>
                  <FaSpinner className="pe-spin" />{' '}
                  {autoDownloadBusy ? 'Downloading…' : 'Export running…'}
                </>
              ) : (
                <>
                  <FaFileExport /> Export Products
                </>
              )}
            </button>
          </div>
        </div>

        {(exportJobStatus === 'pending' || exportJobStatus === 'processing') && (
          <div className="pe-job-status" role="status" aria-live="polite">
            <FaSpinner className="pe-spin pe-spin-sm pe-job-status-icon" aria-hidden />
            <div className="pe-job-status-body">
              {exportJobStatus === 'pending' ? (
                <div className="pe-job-status-line pe-job-status-main">Export: queued — starting shortly.</div>
              ) : (
                <div className="pe-job-status-line pe-job-status-main">
                  Export:{' '}
                  <strong>{exportProcessed.toLocaleString()}</strong>
                  {exportTotalProducts != null && Number.isFinite(exportTotalProducts) ? (
                    <>
                      {' '}
                      of <strong>{exportTotalProducts.toLocaleString()}</strong> matched products
                    </>
                  ) : (
                    <> products (total loading)</>
                  )}
                  . CSV files inside a ZIP (opens in Excel) are generated and uploaded to cloud storage; your ZIP
                  downloads automatically when ready (you can download again from Export jobs).
                </div>
              )}
            </div>
          </div>
        )}
        {autoDownloadBusy ? (
          <div className="pe-job-downloading" role="status" aria-live="polite">
            <FaSpinner className="pe-spin pe-spin-sm pe-job-status-icon" aria-hidden />
            <div className="pe-job-status-body">
              <div className="pe-job-status-line pe-job-status-main">Saving your download…</div>
              <div className="pe-job-status-line pe-job-status-hint">
                Please wait until the file has finished saving to your computer.
              </div>
            </div>
          </div>
        ) : null}
        <BulkSubscribeProgress
          bulkSubJobStatus={bulkSubJobStatus}
          bulkSubProcessed={bulkSubProcessed}
          bulkSubTotalProducts={bulkSubTotalProducts}
        />
        {countStats ? (
          <p className="pe-count-text">
            Matched products: <strong>{countStats.productCount.toLocaleString()}</strong>
            {' | '}
            Variant count: <strong>{countStats.variantCount.toLocaleString()}</strong>
          </p>
        ) : null}

        {errorMessage ? <p className="pe-error">{errorMessage}</p> : null}

        {jobHistoryTab === 'export_jobs' ? (
            <div
              className="pe-export-jobs-wrap"
              role="tabpanel"
              id="pe-job-history-export"
              aria-labelledby="pe-job-tab-export"
            >
          <div className="pe-export-jobs-head">
            <h4 className="pe-export-jobs-title">Export jobs</h4>
            <button
              type="button"
              className="pe-btn-secondary pe-btn-tiny-inline"
              onClick={() => {
                fetchExportJobs();
                fetchSubscribeJobs();
              }}
            >
              <FaSyncAlt /> Refresh
            </button>
          </div>

          {exportJobs.length > 0 && (
            <div className="pe-export-history-scroll">
              <table className="pe-export-history-table">
                <thead>
                  <tr>
                    <th className="pe-export-job-sno-col" scope="col">
                      S.No
                    </th>
                    <th className="pe-export-job-user-head" scope="col">
                      Exported By
                    </th>
                    <th>Status</th>
                    <th>Products</th>
                    <th>Delivered</th>
                    <th>Started</th>
                    <th>Duration</th>
                    <th className="pe-export-job-actions-col" scope="col">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {exportJobs.map((job, index) => {
                    const mod = exportJobStatusBadgeModifiers[job.status] || 'unknown';
                    const exportJobStillRunning =
                      job.status === 'pending' || job.status === 'processing';
                    const showLiveProgress =
                      exportJobStillRunning || Boolean(job.awaitingFileDrop);
                    const productCell = showLiveProgress
                      ? runningProgressFor(job)
                      : Number(job.processedProducts || 0);
                    const jobExportTotal =
                      job.totalProducts != null && Number.isFinite(Number(job.totalProducts))
                        ? Number(job.totalProducts)
                        : null;
                    const jobTotalChunks = job.totalChunks != null ? Number(job.totalChunks) || 1 : 1;
                    const jobChunksDel = Number(job.chunksDelivered || 0);
                    const canRemoveRunningJob = job.status === 'pending' || job.status === 'processing';
                    const canDownloadFromHistory = Boolean(job.awaitingFileDrop);
                    const historyFileName = job.downloadFileName || 'products_export.zip';
                    void zipDownloadedEpoch;
                    const userHasDownloadedZip = zipDownloadedJobIdsRef.current.has(String(job.exportJobId));
                    const deliveredDisplayed =
                      job.delivered || (job.status === 'completed' && userHasDownloadedZip);
                    return (
                      <tr key={job.exportJobId} id={`pe-export-job-row-${job.exportJobId}`}>
                        <td className="pe-export-job-sno-col">{index + 1}</td>
                        <td
                          className="pe-export-job-user-col"
                          title={job.userEmail ? String(job.userEmail) : undefined}
                        >
                          {job.userEmail ? String(job.userEmail) : '—'}
                        </td>
                        <td>
                          <span className={`pe-job-badge pe-job-badge--${mod}`}>
                            {job.status === 'processing' ? (
                              <>
                                <FaSpinner className="pe-spin pe-badge-spin" aria-hidden />{' '}
                                {formatExportJobStatusLabel(job.status)}
                              </>
                            ) : (
                              formatExportJobStatusLabel(job.status)
                            )}
                          </span>
                        </td>
                        <td>
                          {showLiveProgress && jobExportTotal != null ? (
                            <>
                              <strong>{Number(productCell || 0).toLocaleString()}</strong>
                              {' '}
                              <span className="pe-job-status-hint">/ {jobExportTotal.toLocaleString()}</span>
                            </>
                          ) : (
                            Number(productCell || 0).toLocaleString()
                          )}
                        </td>
                        <td>
                          {deliveredDisplayed ? (
                            jobTotalChunks > 1 ? (
                              <span className="pe-export-done-label">{jobTotalChunks} files</span>
                            ) : (
                              <span className="pe-export-done-label">Yes</span>
                            )
                          ) : job.totalChunks != null &&
                            Number(job.totalChunks) > 1 &&
                            (job.status === 'processing' || job.status === 'completed') ? (
                            <span className="pe-job-status-hint">{jobChunksDel} / {jobTotalChunks} files</span>
                          ) : job.status === 'completed' ? (
                            <span className="pe-export-running">Ready</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>{formatExportJobDateTime(job.startedAt)}</td>
                        <td>
                          {exportJobStillRunning
                            ? '—'
                            : formatExportDurationSec(job.startedAt, job.updatedAt)}
                        </td>
                        <td className="pe-export-job-actions-col">
                          <div className="pe-export-job-actions-wrap">
                            <div className="pe-export-job-file-actions">
                              {canDownloadFromHistory ? (
                                <>
                                  <button
                                    type="button"
                                    className="pe-export-job-download pe-export-job-icon-action"
                                    disabled={manualDownloadingJobId === job.exportJobId}
                                    onClick={() => handleHistoryExportDownload(job.exportJobId, historyFileName)}
                                    title="Download ZIP to your computer"
                                  >
                                    {manualDownloadingJobId === job.exportJobId ? (
                                      <FaSpinner className="pe-spin" aria-hidden />
                                    ) : (
                                      <FaDownload aria-hidden />
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    className="pe-export-job-remove pe-export-job-icon-action"
                                    disabled={deletingStoredExportJobId === job.exportJobId}
                                    onClick={() => removeStoredExportFile(job.exportJobId)}
                                    title="Delete ZIP from S3 (and clear any local copy); job stays in history as finished"
                                  >
                                    {deletingStoredExportJobId === job.exportJobId ? (
                                      <FaSpinner className="pe-spin" aria-hidden />
                                    ) : (
                                      <FaTrashAlt aria-hidden />
                                    )}
                                  </button>
                                </>
                              ) : null}
                              <button
                                type="button"
                                className="pe-export-job-view-snapshot pe-export-job-icon-action"
                                onClick={() => setExportJobFilterModalJob(job)}
                                title="View filters and CSV columns used for this export"
                                aria-label="View filters for this export"
                              >
                                <FaEye aria-hidden />
                              </button>
                            </div>
                            {canRemoveRunningJob ? (
                              <button
                                type="button"
                                className="pe-export-job-cancel"
                                disabled={removingJobId === job.exportJobId}
                                onClick={() => cancelActiveExportJob(job.exportJobId)}
                                title="Stop this export; it stays in history as Cancelled"
                              >
                                {removingJobId === job.exportJobId ? (
                                  <FaSpinner className="pe-spin" aria-hidden />
                                ) : (
                                  <FaBan aria-hidden />
                                )}
                                <span className="pe-export-job-cancel-label">Cancel</span>
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {exportJobsHasMore ? (
                    <tr className="pe-export-jobs-load-more-row">
                      <td colSpan={8}>
                        <button
                          type="button"
                          className="pe-btn-secondary pe-export-jobs-load-more"
                          onClick={loadMoreExportJobs}
                          disabled={exportJobsLoadingMore}
                        >
                          {exportJobsLoadingMore ? (
                            <>
                              <FaSpinner className="pe-spin" aria-hidden /> Loading…
                            </>
                          ) : (
                            'Show more'
                          )}
                        </button>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}

          {exportJobs.length === 0 && !exportBusy && (
            <p className="pe-export-history-empty">No export jobs yet. Run &quot;Export Products&quot; to start.</p>
          )}
            </div>
            ) : (
            <SubscriptionHistoryPanel
              history={subscribeHistory}
              bulkSubscribeBusy={bulkSubscribeBusy}
              onRefresh={() => {
                fetchExportJobs();
                fetchSubscribeJobs();
              }}
              cancelActiveExportJob={cancelActiveExportJob}
              removingJobId={removingJobId}
            />
        )}

      </div>

      {exportJobFilterModalJob ? (
        <div
          className="pe-export-fields-overlay pe-job-filter-overlay"
          role="presentation"
          onClick={() => setExportJobFilterModalJob(null)}
        >
          <div
            className="pe-export-fields-sheet pe-job-filter-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pe-job-filter-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pe-export-fields-header">
              <h3 id="pe-job-filter-modal-title">Export run snapshot</h3>
              <button
                type="button"
                className="pe-export-fields-close"
                onClick={() => setExportJobFilterModalJob(null)}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <p className="pe-job-filter-intro">
              Job <strong>{exportJobFilterModalJob.exportJobId}</strong>
              {' · '}
              {formatExportJobDateTime(exportJobFilterModalJob.startedAt)}
              {' · '}
              {formatExportJobStatusLabel(exportJobFilterModalJob.status)}
            </p>
            <div className="pe-export-fields-body pe-job-filter-body">
              <h4 className="pe-job-filter-section-title">Filters at export time</h4>
              {exportJobModalFilterLines.length === 0 ? (
                <p className="pe-job-filter-none">
                  No extra filters were applied — this run used the full product list allowed by your role and export
                  rules.
                </p>
              ) : (
                <div className="pe-job-filter-kv-list" role="list">
                  {exportJobModalFilterLines.map((row, i) => (
                    <div className="pe-job-filter-kv-row" key={`${row.label}-${i}`} role="listitem">
                      <div className="pe-job-filter-k">{row.label}</div>
                      <div className="pe-job-filter-v">{row.value}</div>
                    </div>
                  ))}
                </div>
              )}
              <h4 className="pe-job-filter-section-title">CSV columns in this ZIP</h4>
              {exportJobModalColumns.allFieldsDefault ? (
                <p className="pe-job-filter-none">
                  This job used the default column set — every product and variant field that your export pipeline
                  includes when no custom selection is saved.
                </p>
              ) : (
                <div className="pe-job-filter-columns-wrap">
                  {exportJobModalColumns.product.length > 0 ? (
                    <div className="pe-job-filter-chip-group">
                      <div className="pe-job-filter-chip-heading">Exported Product columns</div>
                      <div className="pe-job-filter-chip-wrap">
                        {exportJobModalColumns.product.map(({ key, label }) => (
                          <span className="pe-job-filter-chip" key={`p-${key}`}>
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {exportJobModalColumns.variant.length > 0 ? (
                    <div className="pe-job-filter-chip-group">
                      <div className="pe-job-filter-chip-heading">Exported Variant columns</div>
                      <div className="pe-job-filter-chip-wrap">
                        {exportJobModalColumns.variant.map(({ key, label }) => (
                          <span className="pe-job-filter-chip pe-job-filter-chip--variant" key={`v-${key}`}>
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <BulkSubscribeJobFilterModal
        job={subscribeHistory.bulkJobFilterModalJob}
        filterLines={subscribeHistory.bulkJobModalFilterLines}
        formatExportJobDateTime={formatExportJobDateTime}
        onClose={() => subscribeHistory.setBulkJobFilterModalJob(null)}
      />

      <BulkSubscribeConfirmModal
        open={bulkSubscribeConfirm.open}
        onClose={bulkSubscribeConfirm.onClose}
        onConfirm={bulkSubscribeConfirm.onConfirm}
        productCount={bulkSubscribeConfirm.productCount}
        sellers={bulkSubscribeConfirm.sellers}
        starting={bulkSubscribeConfirm.starting}
      />

      {exportFieldsOpen ? (
        <div
          className="pe-export-fields-overlay"
          role="presentation"
          onClick={closeExportFieldsModal}
        >
          <div
            className="pe-export-fields-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pe-export-fields-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pe-export-fields-header">
              <h3 id="pe-export-fields-title">Export columns</h3>
              <button
                type="button"
                className="pe-export-fields-close"
                onClick={closeExportFieldsModal}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <p className="pe-export-fields-intro">
              Uncheck any column you do not need in the CSV. One row per variant is only used when at least one variant
              field is selected.
            </p>
            <div className="pe-export-fields-body">
              {exportFieldCatalogLoading ? (
                <div className="pe-export-fields-loading">
                  <FaSpinner className="pe-spin" aria-hidden /> Loading fields…
                </div>
              ) : exportFieldCatalog ? (
                <>
                  <div className="pe-export-fields-panel pe-export-fields-panel-product">
                    <div className="pe-export-fields-panel-head">
                      <h4 className="pe-export-fields-panel-title">Product fields</h4>
                      <p className="pe-export-fields-panel-sub">Columns taken from the product document</p>
                    </div>
                    <div className="pe-export-fields-panel-body">
                      {exportFieldCatalog.productGroups?.map((g) => (
                        <div key={g.groupId} className="pe-export-field-group">
                          <div className="pe-export-field-group-label">{g.groupLabel}</div>
                          <ul className="pe-export-field-list">
                            {g.fields?.map((f) => (
                              <li key={f.key} className="pe-export-field-row">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(productFieldChecked[f.key])}
                                    onChange={() => toggleProductExportField(f.key)}
                                  />
                                  <span>{f.header}</span>
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="pe-export-fields-panel pe-export-fields-panel-variant">
                    <div className="pe-export-fields-panel-head">
                      <h4 className="pe-export-fields-panel-title">Variant fields</h4>
                      <p className="pe-export-fields-panel-sub">One CSV row per variant when any variant column is on</p>
                    </div>
                    <div className="pe-export-fields-panel-body">
                      {exportFieldCatalog.variantGroups?.map((g) => (
                        <div key={g.groupId} className="pe-export-field-group">
                          <div className="pe-export-field-group-label">{g.groupLabel}</div>
                          <ul className="pe-export-field-list">
                            {g.fields?.map((f) => (
                              <li key={f.key} className="pe-export-field-row">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(variantFieldChecked[f.key])}
                                    onChange={() => toggleVariantExportField(f.key)}
                                  />
                                  <span>{f.header}</span>
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <p className="pe-export-fields-error">Could not load field list.</p>
              )}
            </div>
            <div className="pe-export-fields-footer">
              <div className="pe-export-fields-footer-actions">
                <button
                  type="button"
                  className="pe-btn-secondary"
                  onClick={selectAllExportFields}
                  disabled={!exportFieldCatalog || exportFieldCatalogLoading}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="pe-btn-secondary pe-export-fields-btn-unselect"
                  onClick={unselectAllExportFields}
                  disabled={!exportFieldCatalog || exportFieldCatalogLoading}
                >
                  Unselect all
                </button>
              </div>
              <button type="button" className="pe-btn-primary" onClick={closeExportFieldsModal}>
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProductExport;
